import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  computed,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { WORKFLOW_ENTITY_CATALOG } from '@formrails/workflow-designer/data-access';

interface ExpressionGroup {
  label: string;
  tokens: string[];
}

/**
 * Author boolean expressions for Condition / WaitForTrigger nodes. The
 * server uses DynamicExpresso (C#-like syntax) and resolves entities by
 * metadata key. The side panel lists `<Entity>.<Property>` and
 * `<Enum>.<Value>` tokens from the host's catalog; clicking inserts at the
 * cursor.
 */
@Component({
  selector: 'app-expression-editor',
  standalone: true,
  imports: [],
  templateUrl: './expression-editor.component.html',
  styleUrl: './expression-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExpressionEditorComponent {
  private readonly catalog = inject(WORKFLOW_ENTITY_CATALOG);

  readonly value = input<string>('');
  readonly placeholder = input<string>(
    'e.g. Application.Status == ApplicationStatus.Approved'
  );
  readonly rows = input<number>(3);

  readonly valueChange = output<string>();

  readonly textarea = viewChild<ElementRef<HTMLTextAreaElement>>('ta');

  readonly groups = computed<ExpressionGroup[]>(() => {
    const groups: ExpressionGroup[] = [];
    for (const e of this.catalog().entities) {
      groups.push({
        label: e.name,
        tokens: e.properties.map(p => `${e.name}.${p.name}`)
      });
    }
    for (const en of this.catalog().enums) {
      groups.push({
        label: en.name,
        tokens: en.values.map(v => `${en.name}.${v}`)
      });
    }
    return groups;
  });

  onInput(event: Event): void {
    this.valueChange.emit((event.target as HTMLTextAreaElement).value);
  }

  insertToken(token: string): void {
    const ta = this.textarea()?.nativeElement;
    if (!ta) return;
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? start;
    const next = ta.value.slice(0, start) + token + ta.value.slice(end);
    this.valueChange.emit(next);
    queueMicrotask(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  }
}
